import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Lock, Mail } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { fetchSettings } = useSettings();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      await fetchSettings();
      navigate('/dashboard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-zinc-100" data-testid="login-page">
      <Card className="w-[380px] rounded-sm border-zinc-200 shadow-none">
        <CardHeader className="items-center pb-2 pt-8">
          <div className="w-11 h-11 rounded-sm flex items-center justify-center text-sm font-black tracking-tight mb-3"
            style={{ background: 'var(--brand-hero, #1C3557)', color: 'white' }}>
            CB
          </div>
          <CardTitle className="text-xl font-bold tracking-tight text-zinc-900">Sign in to CheckBuild</CardTitle>
          <CardDescription className="text-zinc-500 text-xs">Project start checklists</CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-8">
          {error && (
            <div className="text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-sm mb-4" data-testid="login-error">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                Email address
              </Label>
              <div className="relative">
                <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="login-email" id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  className="pl-9 rounded-sm h-10 text-sm border-zinc-300 focus-visible:ring-[var(--brand-primary)]"
                  placeholder="you@company.com" required />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                Password
              </Label>
              <div className="relative">
                <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <Input data-testid="login-password" id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                  className="pl-9 rounded-sm h-10 text-sm border-zinc-300 focus-visible:ring-[var(--brand-primary)]"
                  placeholder="Enter password" required />
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading}
              className="w-full rounded-sm h-10 text-sm font-semibold"
              style={{ background: 'var(--brand-primary, #1C3557)' }}>
              {loading ? 'Signing in...' : 'Sign in'}
            </Button>
          </form>
          <p className="text-center text-[11px] text-zinc-400 mt-6">Having trouble? Contact your administrator</p>
        </CardContent>
      </Card>
    </div>
  );
}
